// The railway network itself, under the station pins. The basemap tiles do carry
// rail geometry, but only from z11 up — so at every zoom that matters for planning
// a trip the tracks simply aren't there. This draws our own extract instead
// (built by scripts/build-rail.mjs from OSM).
//
// Two overlays, each with its own file and toggle:
//   live    the network you can travel on today. A solid coloured casing with a
//           dashed line of the map's own background over it, which reads as sleepers.
//   closed  what Ireland used to have — disused, abandoned and razed alignments,
//           drawn as a faint sepia ghost underneath. Only fetched if asked for.
import type { GeoJSONSource, Map as MLMap } from "maplibre-gl";
import { DATA_BASE } from "../data/loader";

export const RAIL_SRC = "rail-lines";
export const RAIL_CLOSED_SRC = "rail-closed";

const CASING = "rail-lines-casing";
const HATCH = "rail-lines-hatch";
const GHOST = "rail-closed-ghost";
const DISUSED = "rail-closed-disused";

const FILES: Record<string, string> = { [RAIL_SRC]: "rail.geojson", [RAIL_CLOSED_SRC]: "rail-closed.geojson" };

const EMPTY: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [] };

/** Fetched once and kept, so a theme swap (which rebuilds the style) doesn't refetch. */
const cache = new Map<string, GeoJSON.FeatureCollection>();
const pending = new Map<string, Promise<GeoJSON.FeatureCollection>>();
const visible: Record<string, boolean> = { [RAIL_SRC]: false, [RAIL_CLOSED_SRC]: false };

// Colour by class. `dart` is the electrified Dublin core, `branch` the secondary
// lines, `tram` the Luas, `heritage` the preserved railways you can still ride.
const COLORS: Record<"light" | "dark", Record<string, string>> = {
  light: { dart: "#2f80c9", main: "#1d4e89", branch: "#5b7ea3", tram: "#2b7a78", heritage: "#7d2f2f" },
  dark: { dart: "#6ab0ec", main: "#5a8fd0", branch: "#7d9cbf", tram: "#4fb3ad", heritage: "#c2685f" },
};

// Closed lines are a sepia ghost: rails still in the ground (`disused`) read a
// shade stronger than a trackbed or a line of route that has been built over.
const CLOSED_COLORS: Record<"light" | "dark", { disused: string; gone: string }> = {
  light: { disused: "#6f5946", gone: "#877158" },
  dark: { disused: "#c2ac93", gone: "#9c8a74" },
};

function colorExpr(theme: "light" | "dark"): unknown[] {
  const c = COLORS[theme];
  return ["match", ["get", "cls"], "dart", c.dart, "main", c.main, "branch", c.branch, "tram", c.tram, "heritage", c.heritage, c.main];
}

// Main and DART lines carry the full weight; branches, trams and heritage lines
// are drawn lighter so the trunk network reads first.
const weight = (px: number) => ["match", ["get", "cls"], "dart", px, "main", px, "branch", px * 0.78, "heritage", px * 0.8, px * 0.62];

// `["zoom"]` has to stay at the top level of the expression, so the per-class
// weighting goes inside each stop rather than multiplying the whole interpolation.
const width = (base: number) => [
  "interpolate", ["exponential", 1.5], ["zoom"],
  5, weight(1.0 * base),
  9, weight(1.8 * base),
  12, weight(2.8 * base),
  15, weight(5 * base),
  17.5, weight(8 * base),
];

// The ghost network is deliberately lighter than anything still running.
const closedWidth = (base: number) => [
  "interpolate", ["exponential", 1.5], ["zoom"],
  5, 1.1 * base,
  9, 1.7 * base,
  12, 2.4 * base,
  15, 3.6 * base,
  17.5, 5 * base,
];

const OSM_ATTRIB = 'Track © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> (ODbL)';

function load(src: string): Promise<GeoJSON.FeatureCollection> {
  const hit = cache.get(src);
  if (hit) return Promise.resolve(hit);
  let p = pending.get(src);
  if (!p) {
    const file = FILES[src];
    p = fetch(DATA_BASE + file, { cache: "default" })
      .then((res) => {
        if (!res.ok) throw new Error(`${file}: HTTP ${res.status}`);
        return res.json() as Promise<GeoJSON.FeatureCollection>;
      })
      .then((fc) => { cache.set(src, fc); return fc; })
      .catch((err) => { pending.delete(src); throw err; });
    pending.set(src, p);
  }
  return p;
}

/** Add the sources + layers (idempotent). Call after every style load, like ensureOverlays. */
export function ensureRailLines(map: MLMap, theme: "light" | "dark"): void {
  for (const src of [RAIL_CLOSED_SRC, RAIL_SRC]) {
    if (!map.getSource(src)) {
      map.addSource(src, { type: "geojson", data: cache.get(src) ?? EMPTY, attribution: OSM_ATTRIB });
    }
  }
  const closedVis = visible[RAIL_CLOSED_SRC] ? "visible" : "none";
  const liveVis = visible[RAIL_SRC] ? "visible" : "none";
  const cc = CLOSED_COLORS[theme];

  // The closed network goes in first, so today's lines paint over it where a
  // surviving stretch and a lifted one share the same alignment.
  if (!map.getLayer(GHOST)) {
    map.addLayer({
      id: GHOST,
      type: "line",
      source: RAIL_CLOSED_SRC,
      filter: ["!=", ["get", "cls"], "disused"],
      layout: { "line-cap": "butt", "line-join": "round", visibility: closedVis },
      paint: {
        "line-color": cc.gone,
        "line-width": closedWidth(1) as never,
        "line-dasharray": [2, 2],
        "line-opacity": 0.9,
      },
    });
  }
  if (!map.getLayer(DISUSED)) {
    map.addLayer({
      id: DISUSED,
      type: "line",
      source: RAIL_CLOSED_SRC,
      filter: ["==", ["get", "cls"], "disused"],
      layout: { "line-cap": "butt", "line-join": "round", visibility: closedVis },
      paint: {
        "line-color": cc.disused,
        "line-width": closedWidth(1.15) as never,
        "line-dasharray": [2.6, 1.3],
        "line-opacity": 0.95,
      },
    });
  }
  if (!map.getLayer(CASING)) {
    map.addLayer({
      id: CASING,
      type: "line",
      source: RAIL_SRC,
      layout: { "line-cap": "butt", "line-join": "round", visibility: liveVis },
      paint: {
        "line-color": colorExpr(theme) as never,
        "line-width": width(1) as never,
        "line-opacity": ["interpolate", ["linear"], ["zoom"], 5, 0.85, 9, 0.95],
      },
    });
  }
  if (!map.getLayer(HATCH)) {
    map.addLayer({
      id: HATCH,
      type: "line",
      source: RAIL_SRC,
      // The sleeper ticks only resolve once the casing is a few pixels wide.
      minzoom: 10,
      layout: { "line-cap": "butt", "line-join": "bevel", visibility: liveVis },
      paint: {
        "line-color": theme === "dark" ? "#131a22" : "#f7f5ef",
        "line-width": width(0.55) as never,
        "line-dasharray": [1.4, 1.4],
        "line-opacity": ["interpolate", ["linear"], ["zoom"], 10, 0, 11.5, 0.9],
      },
    });
  }
}

function setVisible(map: MLMap, src: string, layers: string[], on: boolean): void {
  visible[src] = on;
  const vis = on ? "visible" : "none";
  for (const id of layers) if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", vis);
  if (!on || cache.has(src)) return;
  load(src)
    .then((fc) => { (map.getSource(src) as GeoJSONSource | undefined)?.setData(fc); })
    // A missing overlay file is not worth an error banner — the stations still work.
    .catch((err) => { console.warn(`${src} overlay unavailable:`, err); });
}

/** Show or hide today's network, fetching the file on first use. */
export function setRailLinesVisible(map: MLMap, on: boolean): void {
  setVisible(map, RAIL_SRC, [CASING, HATCH], on);
}

/** Show or hide the closed network, fetching the file on first use. */
export function setClosedLinesVisible(map: MLMap, on: boolean): void {
  setVisible(map, RAIL_CLOSED_SRC, [GHOST, DISUSED], on);
}
